import { useCallback, useEffect, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { useGetProposalByProposalIdQuery } from "../../api/gms/proposal/proposal-api";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { usePopulateProposal } from "./use-populate-proposal";
import { usePopulateRates } from "./use-populate-rates";
import { usePopulateRequestAmendment } from "./use-populate-request-amendment";
import { selectAmendmentModalOne, selectedRequestAmendment } from "../../features/actuarial/request-amendment/request-amendment.slice";
import { useConflictProposalDataState } from "../data-state/use-conflict-proposal-data-state";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useProposalData = () => {

    const { isUpdateProposal, isShowActionProposalModal, modalType } = useModalAccess();
    const { conflictProposalId } = useConflictProposalDataState();
    const { currentProposalId } = useModalAccess();
    const amendmentModalOne = useSelector(selectAmendmentModalOne);
    const stopPopulate =  isUpdateProposal || amendmentModalOne === 'isRequesting';
    const requestAmendmentData = useSelector(selectedRequestAmendment);
    const amendProposalId = requestAmendmentData?.application_id;
    const proposalId = amendProposalId || currentProposalId || conflictProposalId;
    const populatedProposalIdRef = useRef<number | null>(null);
    

    const {
        data: proposal,
        isLoading: isLoadingProposal,
        error: errorProposal,
        isError: isErrorProposal,
        refetch: refetchProposal,
        isFetching: isFetchingProposal,
        fulfilledTimeStamp,
    } = useGetProposalByProposalIdQuery(
        proposalId !== null
            ? { proposalId: proposalId }
            : skipToken,
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const proposalErrorMessage = getErrorMessage(errorProposal);
    /*
    |--------------------------------------------------------------------------
    | Proposal population
    |--------------------------------------------------------------------------
    */

    const {
        populateChannelType,
        populateAffiliates,
        populateCommissionRateAndServiceFee,
        populateBasicInfo,
        populateClassification,
        populateBusinessType,
        populateTypeOfGroup,
        populateAgeProfile,
        populateFund,
        populateProposalCategory,
        populateAmountOfLoans,
        populateCoverage,
        populateGYRTorGPA,
        populateRidersPlan,
        populatePayments,
        populateBorrowAge,
        populateNotes,
        populateRiders,
        populateTypeOfProduct,
        populateCSVFile,
        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,
        populateProposalPreparedBy,
        populateProposalStatus,
        populateProposalSupportingDocuments,
        populateApproval,
        populatePolicy,
    } = usePopulateProposal(proposal);

    const repopulatePolicy = useCallback(() => {
        populatePolicy()
    }, [populatePolicy])

    const repopulateAll = useCallback(() => {
        populateChannelType();
        populateAffiliates();
        populateCommissionRateAndServiceFee();
        populateBasicInfo();
        populateClassification();
        populateBusinessType();
        populateTypeOfGroup();
        populateAgeProfile();
        populateFund();
        populateProposalCategory();
        populateAmountOfLoans();
        populateCoverage();
        populateGYRTorGPA();
        populateRidersPlan();
        populatePayments();
        populateBorrowAge();
        populateNotes();
        populateRiders();
        populateTypeOfProduct();
        populateCSVFile();
        populateBorrowerAge1865ForComputation();
        populateProposalRemainingAge();
        populateProposalPreparedBy();
        populateProposalStatus();
        populateProposalSupportingDocuments();
        populateApproval();
    }, [
        populateChannelType,
        populateAffiliates,
        populateCommissionRateAndServiceFee,
        populateBasicInfo,
        populateClassification,
        populateBusinessType,
        populateTypeOfGroup,
        populateAgeProfile,
        populateFund,
        populateProposalCategory,
        populateAmountOfLoans,
        populateCoverage,
        populateGYRTorGPA,
        populateRidersPlan,
        populatePayments,
        populateBorrowAge,
        populateNotes,
        populateRiders,
        populateTypeOfProduct,
        populateCSVFile,
        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,
        populateProposalPreparedBy,
        populateProposalStatus,
        populateProposalSupportingDocuments,
        populateApproval,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Rates population
    |--------------------------------------------------------------------------
    */

    const {
        populateRates,
        populateEvidenceNotes,
        populateTotalAmount,
        populateProposalActuarialFiles,
        populateProposalNotesForPDF,
        populateProposalNotesForCFE,
    } = usePopulateRates(proposal);

    const repopulateRates = useCallback(() => {
        populateRates();
        populateEvidenceNotes();
        populateTotalAmount();
        populateProposalActuarialFiles();
        populateProposalNotesForPDF();
        populateProposalNotesForCFE();
    }, [
        populateRates,
        populateEvidenceNotes,
        populateTotalAmount,
        populateProposalActuarialFiles,
        populateProposalNotesForPDF,
        populateProposalNotesForCFE,
    ]);


    /*
    |--------------------------------------------------------------------------
    | Request amendment population
    |--------------------------------------------------------------------------
    */

    const { populateRequestAmendment } = usePopulateRequestAmendment(proposal);

    const repopulateRequestAmendment = useCallback(() => {
        populateRequestAmendment();
    }, [populateRequestAmendment]);

    const populateProposalPreview = useCallback(() => {
        populateBasicInfo();
        populateChannelType();
        populateTypeOfProduct();
        populateApproval();
        populateProposalPreparedBy();
        populateCSVFile();
        populateProposalSupportingDocuments();
        populateNotes();
        populateProposalCategory();
        populateProposalStatus();
        populateAmountOfLoans();
        populateProposalCategory();
    }, [
        populateBasicInfo,
        populateChannelType,
        populateTypeOfProduct,
        populateApproval,
        populateProposalPreparedBy,
        populateCSVFile,
        populateProposalSupportingDocuments,
        populateNotes,
        populateProposalCategory,
        populateProposalStatus,
        populateAmountOfLoans,
        populateProposalCategory,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Initial API population
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    | Do not use fulfilledTimeStamp here.
    |
    | fulfilledTimeStamp changes whenever RTK Query receives fresh data.
    | If we populate Redux every time it changes, an API refetch can overwrite
    | values that the user has already edited in the form.
    |
    | We only populate when the actual proposal ID changes.
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (isUpdateProposal) {
            return;
        }

        if (amendmentModalOne !== 'isRequesting') {
            return;
        }

        if (!proposal?.application_id) {
            return;
        }

        repopulateAll();
    }, [
        amendmentModalOne,
        proposal?.application_id,
        repopulateAll,
    ]);

    useEffect(() => {
        if (!proposal?.application_id) {
            return;
        }

        if(isUpdateProposal) {
            return;
        }

        if (!isShowActionProposalModal) {
            return;
        }

        populateProposalPreview();
    }, [
        modalType,
        proposal?.application_id,
        fulfilledTimeStamp,
        populateProposalPreview,
    ]);


    return {
        proposal,

        isLoadingProposal,
        errorProposal,
        isErrorProposal,
        proposalErrorMessage,

        refetchProposal,
        isFetchingProposal,

        repopulateAll,

        populateRates,
        populateEvidenceNotes,
        populateTotalAmount,
        repopulateRates,
        populateProposalActuarialFiles,
        populateProposalNotesForPDF,
        populateProposalNotesForCFE,
        populateRequestAmendment,

        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,

        populateApproval,

        repopulatePolicy,
    };
};